import { Fragment, type ReactNode } from "react";
import {
  Dialog as HuiDialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { useModalStackLayer } from "@/lib/modalStack";

export interface ConfirmActionDialogProps {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "warning" | "danger";
  loading?: boolean;
  /** Tailwind z-index-Klasse. Default `z-[250]`. */
  zIndexClass?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmActionDialog({
  open,
  title,
  children,
  confirmLabel = "Bestätigen",
  cancelLabel = "Abbrechen",
  variant = "warning",
  loading = false,
  zIndexClass = "z-[250]",
  onConfirm,
  onCancel,
}: ConfirmActionDialogProps) {
  const { zIndex: stackZIndex } = useModalStackLayer(open);
  return (
    <Transition show={open} as={Fragment}>
      <HuiDialog
        as="div"
        className={clsx("relative", zIndexClass)}
        style={stackZIndex != null ? { zIndex: stackZIndex } : undefined}
        onClose={loading ? () => {} : onCancel}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-[0.99]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.99]"
            >
              <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-ios-lg border border-separator bg-card shadow-ios-md ring-1 ring-black/5 transition">
                <div
                  className={clsx(
                    "border-b border-separator px-5 py-4",
                    variant === "danger" ? "bg-status-red/5" : "bg-status-yellow/10",
                  )}
                >
                  <DialogTitle className="text-base font-semibold text-label">{title}</DialogTitle>
                </div>
                <div className="px-5 py-4 text-sm leading-relaxed text-label-secondary">{children}</div>
                <div className="flex flex-wrap items-center justify-end gap-2 border-t border-separator bg-card-elevated px-5 py-3">
                  <Button type="button" variant="secondary" onClick={onCancel} disabled={loading}>
                    {cancelLabel}
                  </Button>
                  <Button
                    type="button"
                    variant={variant === "danger" ? "danger" : "primary"}
                    onClick={onConfirm}
                    loading={loading}
                  >
                    {confirmLabel}
                  </Button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </HuiDialog>
    </Transition>
  );
}
